"use client";

import { SettingsSection } from "@commerce/components/page";
import { roleNameSchema } from "@repo/api/lib/validation";
import {
	getSystemRolePermissions,
	type PermissionRecord,
	SYSTEM_ROLES,
	type SystemRole,
} from "@repo/auth/permissions";
import { SettingsPageIntro } from "@saas/settings/components/SettingsLayout";
import { errorMessage } from "@saas/settings/lib/errors";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@ui/components/field";
import { InfoTip } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import { ORGANIZATION_MEMBER_ROLES } from "../../hooks/member-roles";
import {
	organizationRolesQueryOptions,
	useCreateRoleMutation,
	useDeleteRoleMutation,
	useUpdateRoleMutation,
} from "../../hooks/use-roles";
import { RolePermissionsGrid } from "./RolePermissionsGrid";

const SYSTEM_ROLE_HINTS: Record<SystemRole, string> = {
	owner: "Everything, including deleting the retailer.",
	admin: "Changes the agent, catalog, team, API keys and settings.",
	member: "Reads everything and uses the playground. Can't publish or change settings.",
};

type Editing =
	| { mode: "create" }
	| { mode: "edit"; id: string; name: string; permissions: PermissionRecord }
	| { mode: "view"; role: SystemRole };

const countPermissions = (permissions: PermissionRecord) =>
	Object.values(permissions).reduce(
		(sum, actions) =>
			sum + ((actions as string[] | undefined)?.length ?? 0),
		0,
	);

export function RolesSettings({ organizationId }: { organizationId: string }) {
	const { data } = useSuspenseQuery(
		organizationRolesQueryOptions(organizationId),
	);
	const deleteRole = useDeleteRoleMutation(organizationId);
	const { confirm } = useConfirmationAlert();
	const [editing, setEditing] = useState<Editing | null>(null);
	const customRoles = data?.roles ?? [];

	return (
		<>
			<SettingsPageIntro
				title="Roles"
				info="Owner, Admin and Member cover most teams. Create a custom role only when someone needs a narrower mix of access."
				actions={
					<Button
						variant="outline"
						onClick={() => setEditing({ mode: "create" })}
					>
						<PlusIcon data-icon="inline-start" />
						New role
					</Button>
				}
			/>

			<SettingsSection title="Built in">
				<ul className="flex flex-col divide-y divide-border/70">
					{SYSTEM_ROLES.map((role) => (
						<li key={role} className="flex items-center gap-3 py-3">
							<div className="min-w-0 flex-1">
								<p className="flex items-center gap-1.5 font-medium text-sm">
									{ORGANIZATION_MEMBER_ROLES[role]}
									<InfoTip>{SYSTEM_ROLE_HINTS[role]}</InfoTip>
								</p>
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									setEditing({ mode: "view", role })
								}
							>
								View access
							</Button>
						</li>
					))}
				</ul>
			</SettingsSection>

			<SettingsSection
				title="Custom"
				description={customRoles.length === 0 ? "None yet." : undefined}
			>
				{customRoles.length > 0 ? (
					<ul className="flex flex-col divide-y divide-border/70">
						{customRoles.map((role) => {
							const permissions =
								role.permission as PermissionRecord;
							return (
								<li
									key={role.id}
									className="flex flex-wrap items-center gap-3 py-3"
								>
									<div className="flex min-w-0 flex-1 items-center gap-2">
										<p className="truncate font-medium font-mono text-sm">
											{role.role}
										</p>
										<Badge variant="secondary">
											{countPermissions(permissions)}{" "}
											permissions
										</Badge>
									</div>
									<div className="flex items-center gap-1">
										<Button
											variant="ghost"
											size="sm"
											onClick={() =>
												setEditing({
													mode: "edit",
													id: role.id,
													name: role.role,
													permissions,
												})
											}
										>
											Edit
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="text-destructive hover:text-destructive"
											onClick={() =>
												confirm({
													title: `Delete the “${role.role}” role?`,
													message:
														"People with this role lose the access it gave them. Give them another role on the Team page first.",
													confirmLabel: "Delete role",
													destructive: true,
													onConfirm: async () => {
														await deleteRole.mutateAsync(
															role.id,
														);
														toast.add({
															type: "success",
															title: `“${role.role}” was deleted`,
														});
													},
												})
											}
										>
											Delete
										</Button>
									</div>
								</li>
							);
						})}
					</ul>
				) : (
					<div className="pb-2" />
				)}
			</SettingsSection>

			<RoleDialog
				key={
					editing
						? editing.mode === "edit"
							? editing.id
							: editing.mode === "view"
								? editing.role
								: "create"
						: "closed"
				}
				organizationId={organizationId}
				editing={editing}
				onClose={() => setEditing(null)}
			/>
		</>
	);
}

export function RolesSettingsSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<Skeleton className="h-10 w-2/3" />
			<Skeleton className="h-48 w-full rounded-3xl" />
			<Skeleton className="h-24 w-full rounded-3xl" />
		</div>
	);
}

function initialPermissions(editing: Editing | null): PermissionRecord {
	if (editing?.mode === "edit") {
		return editing.permissions;
	}
	if (editing?.mode === "view") {
		return getSystemRolePermissions(editing.role) as PermissionRecord;
	}
	return {};
}

function roleDialogTitle(editing: Editing | null) {
	switch (editing?.mode) {
		case "create":
			return "New role";
		case "edit":
			return `Edit “${editing.name}”`;
		case "view":
			return `What ${ORGANIZATION_MEMBER_ROLES[editing.role]}s can do`;
		default:
			return "";
	}
}

/** Creates or updates a custom role; resolves to the success toast's title. */
function useSaveRole(organizationId: string) {
	const create = useCreateRoleMutation(organizationId);
	const update = useUpdateRoleMutation(organizationId);
	const save = async (
		editing: Exclude<Editing, { mode: "view" }>,
		name: string,
		permissions: PermissionRecord,
	) => {
		if (editing.mode === "create") {
			await create.mutateAsync({ name, permissions });
			return `“${name}” role created`;
		}
		await update.mutateAsync({ roleId: editing.id, permissions });
		return `“${editing.name}” updated`;
	};
	return { save, pending: create.isPending || update.isPending };
}

function RoleDialog({
	organizationId,
	editing,
	onClose,
}: {
	organizationId: string;
	editing: Editing | null;
	onClose: () => void;
}) {
	const { save, pending } = useSaveRole(organizationId);
	const [name, setName] = useState("");
	const [nameError, setNameError] = useState<string | null>(null);
	const [permissions, setPermissions] = useState<PermissionRecord>(() =>
		initialPermissions(editing),
	);
	const readOnly = editing?.mode === "view";

	const submit = async (event: FormEvent) => {
		event.preventDefault();
		if (!editing || editing.mode === "view") {
			return;
		}
		if (
			editing.mode === "create" &&
			!roleNameSchema.safeParse(name.trim()).success
		) {
			setNameError(
				"Use lowercase letters, numbers and dashes, like catalog-editor.",
			);
			return;
		}
		try {
			const title = await save(editing, name.trim(), permissions);
			toast.add({ type: "success", title });
			onClose();
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not save the role",
				description: errorMessage(error),
			});
		}
	};

	return (
		<Dialog
			open={editing !== null}
			onOpenChange={(open) => {
				if (!open && !pending) {
					onClose();
				}
			}}
		>
			<DialogContent className="flex max-h-[min(44rem,90svh)] flex-col gap-0 p-0 sm:max-w-2xl">
				<form
					onSubmit={submit}
					className="flex min-h-0 flex-1 flex-col"
				>
					<DialogHeader className="border-border/70 border-b p-6">
						<DialogTitle>{roleDialogTitle(editing)}</DialogTitle>
						<DialogDescription>
							{readOnly
								? "Built-in roles can't be changed."
								: "Tick what this role can do."}
						</DialogDescription>
					</DialogHeader>
					<div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
						{editing?.mode === "create" ? (
							<RoleNameField
								value={name}
								error={nameError}
								onChange={(next) => {
									setName(next);
									setNameError(null);
								}}
							/>
						) : null}
						<RolePermissionsGrid
							value={permissions}
							onChange={setPermissions}
							disabled={readOnly}
						/>
					</div>
					<DialogFooter className="border-border/70 border-t p-4">
						<Button
							type="button"
							variant="ghost"
							onClick={onClose}
							disabled={pending}
						>
							{readOnly ? "Close" : "Cancel"}
						</Button>
						{readOnly ? null : (
							<Button type="submit" disabled={pending}>
								{pending ? (
									<Spinner data-icon="inline-start" />
								) : null}
								{editing?.mode === "create"
									? "Create role"
									: "Save role"}
							</Button>
						)}
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

function RoleNameField({
	value,
	error,
	onChange,
}: {
	value: string;
	error: string | null;
	onChange: (value: string) => void;
}) {
	return (
		<Field data-invalid={error ? true : undefined}>
			<FieldLabel htmlFor="role-name">Name</FieldLabel>
			<Input
				id="role-name"
				placeholder="catalog-editor"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				aria-invalid={error ? true : undefined}
				className="font-mono"
			/>
			{error ? (
				<p role="alert" className="text-destructive text-sm">
					{error}
				</p>
			) : (
				<FieldDescription>
					Lowercase letters, numbers and dashes.
				</FieldDescription>
			)}
		</Field>
	);
}
